import SeoLandingLayout from "@/components/cre/SeoLandingLayout";

const RELATED = [
  { to: "/ai-incident-management", label: "AI incident management" },
  { to: "/aiops-incident-reasoning", label: "AIOps incident reasoning" },
  { to: "/root-cause-analysis-ai", label: "Root cause analysis AI" },
  { to: "/major-incident-management", label: "Major incident management" },
];

const FAQ = [
  {
    q: "How does CREAIOps reduce IT downtime?",
    a: "CREAIOps shortens the reasoning phase of an incident. Faster, better-grounded hypotheses and ranked safe actions help engineers validate and approve a runbook sooner — which directly reduces mean time to recovery without sacrificing change safety.",
  },
  {
    q: "Does reducing downtime mean automating remediation?",
    a: "Not in CREAIOps. The platform is advisory and requires explicit human approval before any action. Downtime is reduced by improving how quickly and confidently humans decide — not by skipping them.",
  },
  {
    q: "Is CREAIOps relevant for highly regulated industries?",
    a: "Yes. CREAIOps maintains an auditable trail of reasoning, recommendations, approvals, and overrides — designed to coexist with regulated change-management processes.",
  },
  {
    q: "How quickly can a team get value from CREAIOps?",
    a: "Teams can start with anonymized incident inputs and the no-login preview the same day. Deeper workflows are adopted as the team builds trust in the reasoning outputs.",
  },
  {
    q: "What does CREAIOps not do?",
    a: "CREAIOps does not run scripts, push changes, or auto-remediate. It is a reasoning and coordination workspace — not a runbook executor. It complements ITSM, AIOps, and observability platforms.",
  },
];

export default function ITDowntimeReduction() {
  return (
    <SeoLandingLayout
      slug="it-downtime-reduction"
      metaTitle="IT Downtime Reduction with AI Reasoning | CREAIOps"
      metaDescription="Reduce IT downtime by accelerating the reasoning phase of major incidents. CREAIOps delivers ranked RCA hypotheses, safe actions, stakeholder updates, and human-approved runbooks — without auto-remediation."
      h1="Reduce IT downtime by accelerating decisions, not skipping them"
      intro="Most downtime in modern IT environments is spent reasoning under pressure: connecting symptoms, weighing blast radius, deciding what to try first, and explaining it to stakeholders. CREAIOps compresses that reasoning phase with multi-agent AI — while keeping every action gated by human approval."
      problem={{
        title:
          "Downtime grows when reasoning is slow, not when execution is slow",
        body:
          "By the time an outage is fully understood, the business has already absorbed the impact. Engineers know what to do — they just need a faster, clearer way to agree on the most likely cause and the safest next step. Without that, every minute of confusion is a minute of downtime.",
        points: [
          "Time-to-hypothesis dominates time-to-recovery on complex incidents",
          "Cross-team coordination slows fastest under pressure",
          "Stakeholder updates compete with technical work for the same attention",
          "Approval bottlenecks magnify when context is poorly communicated",
        ],
      }}
      howWeHelp={{
        title:
          "Compress reasoning time without weakening governance",
        body:
          "CREAIOps dispatches reasoning agents in parallel and gives the incident bridge a ranked view of likely causes, recommended actions, and approval-ready runbooks. Human reviewers approve faster because they receive structured, evidence-backed context — not raw chat scrollback.",
        items: [
          {
            title: "Parallel multi-agent reasoning",
            desc: "Domain agents run simultaneously so the incident manager has a structured picture within minutes.",
          },
          {
            title: "Approval-ready runbook drafts",
            desc: "Reviewers approve with full reasoning context, not just a one-line summary.",
          },
          {
            title: "Aligned communication",
            desc: "Same source of truth for engineering and leadership reduces re-explanation cycles.",
          },
          {
            title: "Audit and governance built in",
            desc: "Every approval and override is captured — speed without sacrificing accountability.",
          },
        ],
      }}
      benefits={[
        "Lower mean time to recovery on major incidents",
        "Fewer escalations driven by missing context",
        "Faster stakeholder communications during outages",
        "Cleaner inputs into problem management for repeat-incident prevention",
        "Reduced cognitive fatigue on senior on-call engineers",
      ]}
      useCase={{
        title:
          "A retailer uses CREAIOps to reduce downtime during peak-season incidents",
        body:
          "During a peak-season checkout outage, the incident bridge typically loses time to re-explanation and partial views. With CREAIOps, the team starts with ranked hypotheses and a recommended action list, and the approver signs off within minutes — measurably reducing customer-impacting downtime.",
        steps: [
          "Anonymized incident brief is submitted to CREAIOps",
          "Multi-agent reasoning produces ranked hypotheses and safe actions",
          "Approval reviewer receives full reasoning context",
          "Approved runbook is executed by on-call engineering",
          "Audit trail and stakeholder narrative are exported",
        ],
      }}
      faq={FAQ}
      related={RELATED}
    />
  );
}
